// Created: 2026-08-14
// Updated: 2026-08-14
//
// USER RELATIONS mutations (react-query-user-relations, Req 7, 8).
//
// Inherited convention (slices 1–6): the MUTATION OWNS THE WRITE — it performs the call through the
// existing transport AND owns the cache effect, so the write+refresh unit lives in one place instead of
// being duplicated across the components that trigger it. Here that replaces two hand-rolled patches of
// provider state: `useFavoritePattern`'s `addFavorite` / `removeFavorite` pair and `Profile.tsx`'s
// `addFollowing` / `removeFollowing` pair, both of which reached into `UserRelationsContext` to keep a
// second copy of the truth in agreement with the server.
//
// ─── WHAT THE TWO SHARE ───────────────────────────────────────────────────────────────────────────
//
//   * `retry: false` on both. A favorite or a follow is a toggle: retrying a rejected one risks sending
//     the SAME transition twice, and it also means a rollback would run against a cache a second and third
//     attempt were still writing. One attempt, one outcome.
//   * A `setQueryData` write against `userRelationsKeys.all()` — never a per-field key, because there
//     isn't one (`keys.ts`).
//   * **NO `onSettled` invalidation, on either one** — see the block below.
//   * Neither takes a `signal`. React Query does not abort mutations, so plumbing one would be a dead
//     parameter, which is also why `followUser` / `unfollowUser` / `addFavoritePattern` /
//     `removeFavoritePattern` were deliberately left without one (Req 11.3).
//   * Both reject with the ORIGINAL error object, untouched, so each surface's existing
//     `console.error`-only catch behaves exactly as it does today and no new user-visible error text
//     appears.
//
// ─── WHY NEITHER INVALIDATES ──────────────────────────────────────────────────────────────────────
//
// **The mutation wrote the new truth itself** (Req 7.4, 8.4). The server's answer to "is pattern P
// favorited by me" after a successful `POST /pattern/P/favorite` is knowable without asking: it is
// `favoriteIds ∪ {P}`. So the `setQueryData` below is not an optimistic guess about a value that must
// later be confirmed — it IS the new value, and invalidating `['me','relations']` afterwards would refetch
// the whole payload in order to replace a correct entry with an identical one. That is also the argument
// for taking the client-wide `staleTime` with no override (see `queries.ts`): the two decisions are the
// same decision.
//
// **AND NOT `profileKeys.author*` EITHER — this is the spot a future editor will reach for it.** Following
// someone changes their `follower_count` and the caller's `following_count`, both of which
// `profileKeys.authorProfile(screenName)` / `authorFollowers(...)` render. Invalidating them from
// `useToggleFollowUserMutation` is the tempting change, and it is NOT made here, because the pre-migration
// `Profile.tsx:226-243` did not refetch them either: it patched provider state and left the counts alone.
// That staleness is recorded as **BL-012** — documented, not fixed — so the slice preserves today's
// behavior exactly rather than smuggling a UX improvement in under a migration. `Profile.tsx`'s four
// `removeQueries` on `profileKeys.author*` (`:167-173`) live in its screen-name-keyed MOUNT effect and stay
// there, untouched and un-duplicated (Req 8.5); do not move them in here and do not add a fifth.
//
// ─── THE ASYMMETRY IS DELIBERATE — DO NOT MAKE THESE TWO MATCH ────────────────────────────────────
//
// The favorite is OPTIMISTIC. The follow is WRITE-ON-SUCCESS. That is not an oversight and not a style
// inconsistency awaiting a tidy-up (design.md § 6): it is the two surfaces' CURRENT behavior, copied.
//
//   * `src/hooks/useFavoritePattern.ts` calls `addFavorite` / `removeFavorite` **BEFORE** its `await` and
//     reverses the pair in `catch`. The heart fills instantly and un-fills if the server refuses. So this
//     mutation gets `onMutate` + snapshot + `onError` rollback — a strictly better version of the same
//     behavior, because it restores ONE snapshot instead of reconstructing an inverse patch.
//   * `Profile.tsx:226-243` **AWAITS** `followUser` / `unfollowUser` and calls `addFollowing` /
//     `removeFollowing` only after the await resolves; its `catch` does nothing but `console.error`, and
//     needs nothing more, because no state was written before the await. The button is
//     `disabled={followLoading}` and reads `Loading...` while the request is in flight (`:349-353`). So
//     that mutation gets no `onMutate`, no snapshot, no rollback, and an `onSuccess` write.
//
// **Uniformity would be a behavior change, not a cleanup.** Giving the follow the favorite's shape would
// replace today's disabled `Loading...` button with an instant flip to `Following` plus a visible flicker
// back when a request fails — a DIFFERENT Follow button on the highest-traffic authenticated page, and a
// fourth intentional delta nobody asked for. The rule this sets for later slices: a migration copies the
// optimism the surface already has, because **optimism is a UX decision, not a transport decision.** It
// trades a guaranteed-correct render for a faster-feeling one, and that trade belongs to whoever owns the
// surface, not to whoever is moving the fetch. When one module holds two writes with different optimism,
// the asymmetry IS the requirement.
//
// `__tests__/mutations.test.tsx` pins this: the follow's "cache unchanged while the request is in flight"
// assertion is the ONE assertion an optimistic implementation fails (it would pass both the success and
// the failure halves, since `onMutate` + `onError`-restore ends in the same two places), and the
// favorite's rollback is asserted as a WHOLE-OBJECT `toEqual` against the snapshot, which a patch-inverse
// rollback fails. Both are mutation-checked.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import { addFavoritePattern, removeFavoritePattern } from '../../api/secure/patterns'
import { followUser, unfollowUser } from '../../api/secure/social'

import { userRelationsKeys } from './keys'
import type { WebsiteUserRelations } from './types'

/**
 * Favorite toggle input. `nextFavorited` is the INTENT, not the current state.
 *
 * Passing the intent rather than reading the current cache inside `mutationFn` is what keeps the transport
 * choice and the optimistic write derived from the SAME value, so a double-click cannot send `add` while
 * the cache is written as removed.
 */
export type FavoriteToggleVars = { patternId: string; nextFavorited: boolean }

/** Follow toggle input. `nextFollowing` is the intent, for the same reason as above. */
export type FollowToggleVars = { screenName: string; nextFollowing: boolean }

/**
 * Rollback context for the OPTIMISTIC favorite — the pre-mutation cache value, captured by reference and
 * restored verbatim.
 *
 * `previous` is `undefined` when there was no cached entry at click time (a signed-out user cannot reach
 * the transport, but an entry purged mid-flight can produce it). In that case `onMutate` writes nothing,
 * so `onError` has nothing to restore, and the two stay in agreement.
 *
 * This type is the FAVORITE's context and only the favorite's. `useToggleFollowUserMutation` deliberately
 * has no fourth type parameter — a mutation that never wrote before settling has nothing to restore.
 */
export type RelationsRollback = { previous: WebsiteUserRelations | undefined }

/** Add `value` to `list` at most once — the idempotent add both writes use. */
function withMember(list: string[] | undefined, value: string): string[] {
  const current = Array.isArray(list) ? list : []
  return current.includes(value) ? current : [...current, value]
}

/** Remove every occurrence of `value` from `list` — the removal both writes use. */
function withoutMember(list: string[] | undefined, value: string): string[] {
  const current = Array.isArray(list) ? list : []
  return current.filter((member) => member !== value)
}

/**
 * Toggle a pattern favorite. Caller: `src/hooks/useFavoritePattern.ts`, which is called from every pattern
 * card and every pattern detail page.
 *
 * **OPTIMISTIC** (Req 7.2–7.4), because `useFavoritePattern` is optimistic today — see the asymmetry block
 * in the module header before changing this.
 *
 * `nextFavorited` selects the transport (`true` → `addFavoritePattern`, `false` →
 * `removeFavoritePattern`, both `src/api/secure/patterns.ts`) **and** the direction of the optimistic
 * write, so the two cannot disagree.
 *
 * `onMutate` does three things in order: `cancelQueries` (so an in-flight refetch cannot land on top of the
 * optimistic write and undo it), snapshot into `RelationsRollback`, then write. `onError` restores the
 * **EXACT SNAPSHOT** verbatim — not an inverse patch. That distinction is the whole reason the payload is
 * cached verbatim with every coercion derived: restoring one object restores every derived member at once,
 * whereas an inverse patch has to reconstruct each copy independently and happens to be right here only
 * because favorites are a set of strings.
 */
export function useToggleFavoritePatternMutation(
  idToken: string | null | undefined,
): UseMutationResult<void, Error, FavoriteToggleVars, RelationsRollback> {
  const queryClient = useQueryClient()

  return useMutation<void, Error, FavoriteToggleVars, RelationsRollback>({
    mutationFn: async ({ patternId, nextFavorited }) => {
      if (!idToken) throw new Error('Cannot toggle a favorite while signed out')
      if (nextFavorited) {
        await addFavoritePattern(patternId, idToken)
      } else {
        await removeFavoritePattern(patternId, idToken)
      }
    },
    onMutate: async ({ patternId, nextFavorited }): Promise<RelationsRollback> => {
      // An in-flight refetch would otherwise resolve AFTER the optimistic write and silently revert it.
      await queryClient.cancelQueries({ queryKey: userRelationsKeys.all() })

      const previous = queryClient.getQueryData<WebsiteUserRelations>(userRelationsKeys.all())

      if (previous) {
        const optimistic: WebsiteUserRelations = {
          ...previous,
          favoriteIds: nextFavorited
            ? withMember(previous.favoriteIds, patternId)
            : withoutMember(previous.favoriteIds, patternId),
        }
        queryClient.setQueryData<WebsiteUserRelations>(userRelationsKeys.all(), optimistic)
      }

      // No entry to patch means nothing was written, so `onError` will have nothing to restore.
      return { previous }
    },
    onError: (_error, _vars, context) => {
      // THE EXACT SNAPSHOT, verbatim. Not `favoriteIds` alone, and not an inverse patch — a whole-object
      // restore is what makes every derived member (favoriteSet, isFavorited) correct in one step, and it
      // is what `__tests__` asserts with a whole-object `toEqual`.
      queryClient.setQueryData<WebsiteUserRelations>(userRelationsKeys.all(), context?.previous)
    },
    // One attempt. A retried toggle risks sending the same transition twice, and would race the rollback.
    retry: false,
    // NO `onSettled` invalidation — the write above IS the new truth. See the module header.
  })
}

/**
 * Toggle a follow. Caller: `Profile.tsx`'s `handleFollowToggle`.
 *
 * **WRITE-ON-SUCCESS** (Req 8.2), because `Profile.tsx:226-243` is await-then-write today. There is **no
 * `onMutate`, no snapshot, no rollback path, and no fourth type parameter** — a mutation that never wrote
 * before settling has nothing to restore, so `RelationsRollback` does not appear here. On failure the cache
 * is untouched, and the only observable is the `console.error` `Profile.tsx` already performs (Req 8.4).
 *
 * Do **not** "align" this with the favorite above. The asymmetry block in the module header is the reason,
 * and the in-flight assertion in `__tests__/mutations.test.tsx` is the guard.
 *
 * `nextFollowing` selects the transport (`true` → `followUser`, `false` → `unfollowUser`, both
 * `src/api/secure/social.ts`) **and** the direction of the `onSuccess` write. The add is idempotent so a
 * repeated follow cannot double a name; the removal filters, so it cannot leave a duplicate behind.
 */
export function useToggleFollowUserMutation(
  idToken: string | null | undefined,
): UseMutationResult<void, Error, FollowToggleVars> {
  const queryClient = useQueryClient()

  return useMutation<void, Error, FollowToggleVars>({
    mutationFn: async ({ screenName, nextFollowing }) => {
      if (!idToken) throw new Error('Cannot toggle a follow while signed out')
      if (nextFollowing) {
        await followUser(screenName, idToken)
      } else {
        await unfollowUser(screenName, idToken)
      }
    },
    onSuccess: (_result, { screenName, nextFollowing }) => {
      // The write happens HERE — after the await resolved — exactly where `Profile.tsx:226-243` writes
      // today. Nothing was written before this point, which is why there is no rollback anywhere below.
      queryClient.setQueryData<WebsiteUserRelations>(userRelationsKeys.all(), (previous) => {
        if (!previous) return previous
        return {
          ...previous,
          followingScreenNames: nextFollowing
            ? withMember(previous.followingScreenNames, screenName)
            : withoutMember(previous.followingScreenNames, screenName),
        }
      })
    },
    // One attempt, same reason as the favorite.
    retry: false,
    // NO `onSettled` invalidation, and NO `profileKeys.author*` invalidation — see the module header for
    // why the follower-count staleness is BL-012 rather than a bug fixed here.
  })
}
